"use client";
import React, { useEffect, useRef, useState } from "react";
import { Button, Empty, Spin } from "antd";
import Image from "next/image";
import { Link } from "@/navigation";
import { PlusCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import type { Blog } from "@/lib/@types/api/blog";
import { getBlogList } from "@/lib/api/blog";
import moment from "moment/moment";
import { useTranslations } from "next-intl";
import { replaceImgUrl } from "@/lib/utils.ts";

const ProductKnowledge = ({ locale }) => {
	const t = useTranslations();
	const [blogList, setBlogList] = useState<Blog.BlogListItem[]>([]);
	const [pageSize, setPageSize] = useState(1);
	const [searchLoading, setSearchLoading] = useState(false);
	const limit = 12;

	const fetchBlogList = async (
		Params: Blog.GetBlogListParams,
	): Promise<{ blogList: Blog.BlogListItem[]; blogCount: number }> => {
		setSearchLoading(true);
		try {
			const res = await getBlogList(Params);
			setSearchLoading(false);
			return { blogList: res.detail.blog_list, blogCount: res.detail.blog_filter_count };
		} catch (error) {
			setSearchLoading(false);
			console.error("Error fetching blog list:", error);
			return { blogList: [], blogCount: 0 }; // 如果发生错误，返回一个空数组
		}
	};

	useEffect(() => {
		fetchBlogList({
			lang_code: { lang_code: locale },
			page: pageSize,
			limit,
			blog_title: "",
		}).then((res) => {
			if (res) {
				setBlogList(res.blogList);
			}
		});
	}, []);

	return (
		<div className="container py-16 max-md:py-8">
			<div className="flex flex-col gap-[12px]">
				<div className="text-center text-[3rem] font-bold leading-normal text-[#212121] max-md:text-2xl">
					{t("resource.Product Knowledge")}
				</div>
				<div className="flex flex-row items-center justify-end gap-[12px] max-md:flex-col">
					<div className="flex justify-end max-md:w-full">
						<Link
							className="flex cursor-pointer justify-end gap-[20px] text-[#212121] hover:text-[#0e6aed] max-md:gap-[8px]"
							href="/blog"
						>
							<span className="whitespace-nowrap text-[20px] max-md:text-[14px]">
								{t("resource.View More")}
							</span>
							<PlusCircleOutlined className="text-[26px] max-md:text-[18px]" />
						</Link>
					</div>
				</div>
			</div>
			<div className="mt-8 grid grid-cols-1 gap-[35px]">
				{searchLoading ? (
					<Spin size="large" />
				) : blogList?.length > 0 ? (
					blogList?.slice(0, 3).map((item: any, index: number) => {
						{
							return (
								<Link
									href={"/blog/" + item.blog_slug}
									className="knowledge-item flex cursor-pointer flex-row-reverse items-center gap-[80px] max-md:flex-col max-md:gap-[20px]"
									key={index}
								>
									<div className="flex w-[290px] shrink-0 flex-col items-center justify-center overflow-hidden rounded-[15px] border border-[#c1c1c1]">
										<img className="h-full w-full object-cover" src={replaceImgUrl(item.blog_cover_origin)} />
									</div>
									<div className="flex flex-row items-center gap-20 border-b border-[#c1c1c1] pb-[46px] max-md:gap-4 max-md:pb-[20px]">
										<div className="flex items-center gap-3 text-[#212121] max-md:gap-2">
											<div className="flex items-center">
												<div className="text-[40px] font-bold max-md:text-[32px]">
													{moment(item.blog_upload_time).format("DD")}
												</div>
												<div className="text-[32px] font-bold max-md:text-[24px]">/</div>
											</div>
											<div className="text-[12px] text-[#212121]">
												<div>{moment(item.blog_upload_time).format("MMM")}</div>
												<div>{moment(item.blog_upload_time).format("YYYY")}</div>
											</div>
										</div>
										<div className="flex-auto">
											<h3 className="knowledge-item-text line-clamp-1 !text-xl font-bold text-black md:my-2">
												{item.blog_title}
											</h3>
											<p className="line-clamp-1 text-xs font-normal text-[#677E6E] max-md:my-1 sm:text-sm md:my-2">
												{item.blog_excerpt}
											</p>
										</div>
									</div>
								</Link>
							);
						}
					})
				) : (
					<Empty />
				)}
			</div>
		</div>
	);
};

export default ProductKnowledge;
